import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PharmSidebar } from '../pharm-sidebar/pharm-sidebar';
import { PharmHeader } from '../pharm-header/pharm-header';

const baseUrl = 'https://logs.proofile.com.ng/api';

@Component({
  selector: 'app-pharm-patients',
  standalone: true,
  imports: [CommonModule, FormsModule, PharmHeader, PharmSidebar],
  templateUrl: './pharm-patients.html',
  styleUrl: './pharm-patients.css'
})
export class PharmPatients implements OnInit {
  patients: any[] = [];
  wards: any[] = [];
  companies: any[] = [];

  filters = {
    surname: '',
    other_names: '',
    sex: '',
    hospital_id: '',
    phone: '',
    email: '',
    type: '',
    ward_id: ''
  };

  loading = false;
  feedbackMessage = '';
  feedbackType: 'success' | 'error' = 'success';

  newCompanyName = '';
  currentPage = 1;
  pageSize = 10;

  showPatientModal = false;
  form = {
    surname: '',
    other_names: '',
    sex: '',
    age: null,
    hospital_id: '',
    allergic: '',
    phone: '',
    email: '',
    type: 'out-patient',
    ward_id: null,
    company_id: null
  };

  editingId: number | null = null;

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit() {
    this.fetchPatients();
    this.fetchWards();
    this.fetchCompanies();
  }

  fetchPatients() {
    this.loading = true;
    this.http.get<any[]>(`${baseUrl}/patients`).subscribe({
      next: data => {
        this.patients = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showMessage('❌ Failed to load patients', 'error');
      }
    });
  }

  fetchWards() {
    this.http.get<any[]>(`${baseUrl}/wards`).subscribe(data => {
      this.wards = data;
    });
  }

  fetchCompanies() {
    this.http.get<any[]>(`${baseUrl}/companies`).subscribe(data => {
      this.companies = data;
    });
  }

  getWardName(id: number): string {
    const ward = this.wards.find(w => w.id === id);
    return ward ? ward.name : 'N/A';
  }

  getCompanyName(id: number): string {
    const company = this.companies.find(c => c.id === id);
    return company ? company.name : '—';
  }

  get filteredPatients() {
    return this.patients.filter(p =>
      (!this.filters.surname || p.surname.toLowerCase().includes(this.filters.surname.toLowerCase())) &&
      (!this.filters.other_names || p.other_names.toLowerCase().includes(this.filters.other_names.toLowerCase())) &&
      (!this.filters.sex || p.sex.toLowerCase().includes(this.filters.sex.toLowerCase())) &&
      (!this.filters.hospital_id || p.hospital_id.toLowerCase().includes(this.filters.hospital_id.toLowerCase())) &&
      (!this.filters.phone || p.phone.toLowerCase().includes(this.filters.phone.toLowerCase())) &&
      (!this.filters.email || p.email.toLowerCase().includes(this.filters.email.toLowerCase())) &&
      (!this.filters.type || p.type === this.filters.type) &&
      (!this.filters.ward_id || p.ward_id === this.filters.ward_id)
    );
  }

  get paginatedPatients() {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredPatients.slice(start, start + this.pageSize);
  }

  resetFilters() {
    this.filters = {
      surname: '',
      other_names: '',
      sex: '',
      hospital_id: '',
      phone: '',
      email: '',
      type: '',
      ward_id: ''
    };
    this.currentPage = 1;
  }

  trackById(index: number, item: any) {
    return item.id;
  }

  submit() {
    const url = this.editingId
      ? `${baseUrl}/patients/${this.editingId}`
      : `${baseUrl}/patients`;

    const request = this.editingId
      ? this.http.put(url, this.form)
      : this.http.post(url, this.form);

    request.subscribe({
      next: () => {
        this.resetForm();
        this.fetchPatients();
        this.showMessage('✅ Patient saved successfully!', 'success');
        this.showPatientModal = false;
      },
      error: () => this.showMessage('❌ Failed to save patient.', 'error')
    });
  }

  edit(p: any) {
    this.editingId = p.id;
    this.form = { ...p };
    this.showPatientModal = true;
  }

  delete(id: number) {
    if (confirm('Are you sure you want to delete this patient?')) {
      this.http.delete(`${baseUrl}/patients/${id}`).subscribe({
        next: () => {
          this.fetchPatients();
          this.showMessage('🗑️ Patient deleted.', 'success');
        },
        error: () => this.showMessage('❌ Failed to delete patient.', 'error')
      });
    }
  }

  resetForm() {
    this.form = {
      surname: '',
      other_names: '',
      sex: '',
      age: null,
      hospital_id: '',
      allergic: '',
      phone: '',
      email: '',
      type: 'out-patient',
      ward_id: null,
      company_id: null
    };
    this.editingId = null;
  }

  viewDetails(id: number) {
    this.router.navigate(['/admin/patients', id]);
  }

  showMessage(msg: string, type: 'success' | 'error') {
    this.feedbackMessage = msg;
    this.feedbackType = type;
    setTimeout(() => (this.feedbackMessage = ''), 4000);
  }

  exportToCSV() {
    const headers = ['Surname', 'Other Names', 'Sex', 'Age', 'Hospital ID', 'Phone', 'Email', 'Type', 'Ward', 'Company'];
    const rows = this.filteredPatients.map(p => [
      p.surname, p.other_names, p.sex, p.age, p.hospital_id,
      p.phone, p.email, p.type,
      this.getWardName(p.ward_id),
      this.getCompanyName(p.company_id)
    ]);

    const csv = [headers, ...rows].map(r => r.map(val => `"${val}"`).join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `patients_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
